import React, { useState, useEffect } from 'react';

const SmsManagement = () => {
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [frequency, setFrequency] = useState('weekly');
  const [linkedPhone, setLinkedPhone] = useState('');
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Load phone from contact form localStorage
    const contactData = localStorage.getItem('contactFormPhone');
    if (contactData) setLinkedPhone(contactData);

    // Load SMS preferences
    const smsPrefs = localStorage.getItem('smsManagementPrefs');
    if (smsPrefs) {
      const prefs = JSON.parse(smsPrefs);
      setSmsEnabled(prefs?.smsEnabled ?? false);
      setFrequency(prefs?.frequency ?? 'weekly');
    }
  }, []);

  const handleToggle = () => {
    setSmsEnabled(prev => !prev);
    setHasChanges(true);
    setSaved(false);
  };

  const handleFrequency = (val) => {
    setFrequency(val);
    setHasChanges(true);
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem('smsManagementPrefs', JSON.stringify({ smsEnabled, frequency }));
    setHasChanges(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const frequencies = [
    { id: 'daily', label: 'Daglig', desc: 'Opptil 1 melding per dag' },
    { id: 'weekly', label: 'Ukentlig', desc: 'Opptil 1 melding per uke' },
    { id: 'monthly', label: 'Månedlig', desc: 'Opptil 1 melding per måned' },
  ];

  return (
    <div style={{ position: 'relative' }}>
      {/* Section header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,87,34,0.08)', border: '1px solid rgba(255,87,34,0.2)', borderRadius: '2px', padding: '4px 12px', marginBottom: '16px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF5722" strokeWidth="2.5">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF5722' }}>SMS-administrasjon</span>
        </div>
        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '8px', letterSpacing: '0.01em' }}>SMS-innstillinger</h2>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--color-muted-foreground)', lineHeight: 1.6 }}>Administrer SMS-markedsføring, frekvens og tilknyttet telefonnummer.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>

        {/* Linked Phone Number */}
        <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: 'rgba(255,87,34,0.04)', transform: 'rotate(45deg) translate(30px, -30px)', pointerEvents: 'none' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{ width: '36px', height: '36px', background: 'rgba(255,87,34,0.1)', border: '2px solid rgba(255,87,34,0.25)', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF5722" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                </div>
                <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 700, color: 'var(--color-foreground)', letterSpacing: '0.02em' }}>Tilknyttet telefonnummer</h3>
              </div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--color-muted-foreground)', lineHeight: 1.5, marginBottom: '16px' }}>
                Telefonnummeret hentet fra kontaktskjemaet ditt. Oppdater det i kontaktskjemaet for å endre.
              </p>
              {linkedPhone ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: 'rgba(255,87,34,0.08)', border: '2px solid rgba(255,87,34,0.3)', borderRadius: '2px', padding: '10px 18px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF5722" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
                  </svg>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 700, color: '#FF5722', letterSpacing: '0.04em' }}>{linkedPhone}</span>
                </div>
              ) : (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(156,163,175,0.08)', border: '1px dashed rgba(156,163,175,0.3)', borderRadius: '2px', padding: '10px 18px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)' }}>Ingen telefon registrert — fyll ut kontaktskjemaet</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SMS Opt-in Toggle */}
        <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-1px', left: '-1px', width: '4px', height: '100%', background: smsEnabled ? '#FF5722' : 'rgba(156,163,175,0.2)', transition: 'background 300ms ease' }} />
          <div style={{ paddingLeft: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '6px', letterSpacing: '0.02em' }}>SMS-markedsføring</h3>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>
                  Motta eksklusive tilbud, bomtakst-varsler og tjenesteoppdateringer via SMS.
                </p>
              </div>
              {/* Toggle Switch */}
              <button
                onClick={handleToggle}
                aria-pressed={smsEnabled}
                style={{
                  position: 'relative',
                  width: '56px',
                  height: '28px',
                  background: smsEnabled ? '#FF5722' : 'rgba(156,163,175,0.2)',
                  border: `2px solid ${smsEnabled ? '#FF5722' : 'rgba(156,163,175,0.3)'}`,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  transition: 'all 250ms ease',
                  flexShrink: 0,
                  outline: 'none',
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: smsEnabled ? '26px' : '2px',
                  width: '20px',
                  height: '20px',
                  background: '#FFFFFF',
                  borderRadius: '50%',
                  transition: 'left 250ms ease',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }} />
              </button>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: smsEnabled ? '#22C55E' : 'rgba(156,163,175,0.4)', transition: 'background 250ms ease' }} />
              <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', fontWeight: 500, color: smsEnabled ? '#22C55E' : 'var(--color-muted-foreground)', transition: 'color 250ms ease' }}>
                {smsEnabled ? 'SMS-varsler er aktivert' : 'SMS-varsler er deaktivert'}
              </span>
            </div>
          </div>
        </div>

        {/* Frequency Preferences */}
        <div className="card" style={{ opacity: smsEnabled ? 1 : 0.5, transition: 'opacity 300ms ease', pointerEvents: smsEnabled ? 'auto' : 'none' }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '6px', letterSpacing: '0.02em' }}>Meldingsfrekvens</h3>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--color-muted-foreground)', marginBottom: '20px', lineHeight: 1.5 }}>Velg hvor ofte du ønsker å motta SMS-meldinger fra oss.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
            {frequencies?.map((f) => (
              <button
                key={f?.id}
                onClick={() => handleFrequency(f?.id)}
                style={{
                  padding: '16px 20px',
                  background: frequency === f?.id ? 'rgba(255,87,34,0.1)' : 'rgba(255,255,255,0.02)',
                  border: `2px solid ${frequency === f?.id ? '#FF5722' : 'rgba(224,228,232,0.12)'}`,
                  borderRadius: '2px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 200ms ease',
                  position: 'relative',
                  outline: 'none',
                }}
              >
                {frequency === f?.id && (
                  <div style={{ position: 'absolute', top: '-1px', right: '-1px', width: '16px', height: '16px', borderTop: '3px solid #FF5722', borderRight: '3px solid #FF5722' }} />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  {frequency === f?.id && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF5722" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', fontWeight: 700, color: frequency === f?.id ? '#FF5722' : 'var(--color-foreground)', letterSpacing: '0.02em' }}>{f?.label}</span>
                </div>
                <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>{f?.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            style={{
              background: hasChanges ? '#FF5722' : 'rgba(156,163,175,0.15)',
              border: `2px solid ${hasChanges ? '#FF5722' : 'rgba(156,163,175,0.2)'}`,
              borderRadius: '2px',
              padding: '14px 32px',
              fontFamily: 'var(--font-heading)',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: hasChanges ? '#FFFFFF' : 'var(--color-muted-foreground)',
              cursor: hasChanges ? 'pointer' : 'not-allowed',
              transition: 'all 200ms ease',
              outline: 'none',
            }}
          >
            Lagre innstillinger
          </button>
          {saved && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '2px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: '#22C55E', fontWeight: 500 }}>Innstillinger lagret!</span>
            </div>
          )}
        </div>

        {/* Consent note */}
        <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)', lineHeight: 1.6, borderTop: '1px solid rgba(224,228,232,0.08)', paddingTop: '16px' }}>
          Ved å aktivere SMS-varsler samtykker du til å motta markedsføringsmeldinger fra Veiassistent. Du kan når som helst avslutte ved å svare STOPP på en SMS eller deaktivere her. Maks {frequency === 'daily' ? '1 melding per dag' : frequency === 'weekly' ? '1 melding per uke' : '1 melding per måned'}.
        </p>
      </div>
    </div>
  );
};

export default SmsManagement;
